import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Sparkles } from 'lucide-react';
import { aiAPI } from '../services/api';

const VirtualSalesAgent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: "Hello! I'm your **Virtual Sales Agent**, grounded in your live sales data.\n\nAsk me anything about:\n- Lead status and follow-ups\n- Sales pipeline and stages\n- BDE performance\n- Post-sales activities\n- Client analytics"
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSend = async () => {
    const question = input.trim();
    if (!question || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: question }]);
    setLoading(true);

    try {
      const history = messages
        .filter(m => m.role !== 'system')
        .map(m => ({ role: m.role, content: m.text }));

      const response = await aiAPI.chat(question, history);

      if (response?.success && response?.answer) {
        setMessages(prev => [...prev, { role: 'assistant', text: response.answer }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', text: 'Sorry, I couldn\'t process that. Please try again.' }]);
      }
    } catch (err) {
      console.error('Virtual Sales Agent error:', err);
      setMessages(prev => [...prev, { role: 'assistant', text: 'Something went wrong. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const renderMarkdown = (text) => {
    return text
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n- /g, '\n• ')
      .replace(/\n/g, '<br/>');
  };

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg flex items-center justify-center z-50 transition-transform hover:scale-110 group"
        style={{ background: 'linear-gradient(135deg, #86bb46 0%, #679632 100%)' }}
        title="Virtual Sales Agent"
      >
        <Sparkles className="w-7 h-7 text-white" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 lg:bottom-6 lg:right-6 z-50 flex flex-col w-[calc(100vw-2rem)] sm:w-[380px] lg:w-[400px]" style={{ maxHeight: '80vh' }}>
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3 rounded-t-xl text-white"
        style={{ background: 'linear-gradient(135deg, #86bb46 0%, #679632 100%)' }}
      >
        <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">Virtual Sales Agent</p>
          <p className="text-xs text-white/80 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-green-300 inline-block" />
            Live — Grounded in your data
          </p>
        </div>
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto bg-white px-4 py-3 space-y-3 border-x border-gray-200" style={{ maxHeight: '380px', minHeight: '200px' }}>
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role === 'assistant' && (
              <div className="w-7 h-7 rounded-full bg-[#679632]/10 flex items-center justify-center flex-shrink-0 mr-2 mt-0.5">
                <Sparkles size={14} className="text-[#679632]" />
              </div>
            )}
            <div
              className={`max-w-[85%] px-3 py-2 rounded-xl text-sm leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-[#679632] text-white rounded-br-sm'
                  : 'bg-gray-100 text-gray-800 rounded-bl-sm border border-gray-200'
              }`}
              dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.text) }}
            />
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="w-7 h-7 rounded-full bg-[#679632]/10 flex items-center justify-center flex-shrink-0 mr-2 mt-0.5">
              <Sparkles size={14} className="text-[#679632]" />
            </div>
            <div className="bg-gray-100 text-gray-500 px-4 py-2 rounded-xl rounded-bl-sm border border-gray-200 text-sm">
              <span className="inline-flex gap-1">
                <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
              </span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick actions */}
      <div className="bg-white px-3 py-2 border-x border-gray-200 flex flex-wrap gap-1.5">
        {['Pipeline summary', 'Overdue follow-ups', 'Today\'s leads'].map(q => (
          <button
            key={q}
            type="button"
            onClick={() => { setInput(q); }}
            className="text-xs px-2.5 py-1 rounded-full border border-[#679632]/30 text-[#679632] hover:bg-[#679632]/5 transition-colors"
          >
            {q}
          </button>
        ))}
      </div>

      {/* Input */}
      <div className="flex items-center gap-2 bg-white px-3 py-3 border border-gray-200 rounded-b-xl">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask anything about your data..."
          className="flex-1 text-sm outline-none placeholder-gray-400 bg-transparent"
          disabled={loading}
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={loading || !input.trim()}
          className="p-2 rounded-lg text-gray-400 hover:text-[#679632] disabled:opacity-30 transition-colors"
        >
          <Send size={18} />
        </button>
      </div>

      {/* Shadow */}
      <style>{`
        .fixed { box-shadow: 0 8px 32px rgba(0,0,0,0.15); border-radius: 12px; }
      `}</style>
    </div>
  );
};

export default VirtualSalesAgent;
